import { FC, useEffect, useRef, useContext, useState } from 'react'
import clsx from 'clsx'

import TranslationPlayer from '../translation-player/translation-player'
import TranslationSoundButton from '../translation-sound-button/translation-sound-button'
import statisticContext from 'context/statisticsContext'
import WebinarSocketContext from 'context/webinarSocketContext'
import PlayerControls from '../controls/player-controls'
import useFullscreen from '../translation-hooks/useFullscreen'
import usePCWindow from 'hooks/usePCWindow'
import useDeviceOrientation, { ScreenOrientation } from 'hooks/useDeviceOrientation'
import useIsHideControls from '../translation-hooks/useIsHideControls'
import useVisibleUnmute from '../useVisibleUnmute'

import { IAnyObj } from 'constants/types/common.types'
import { PlayerStatus, statisticEvent, YandexPlayerStatuses } from '../../data'
import { StatusVocabulary } from 'constants/data'
import {
  CONTROLS_SHOW_TIME,
  CONTROLS_FIRST_SHOW_TIME,
  PlayerEvent,
} from './yandex-translation-constants'

import iframeStyle from '../../iframe.module.scss'

interface IRutubeVideo {
  videoLink: string
}

const YandexTranslation: FC<IRutubeVideo> = ({ videoLink }) => {
  const isPC = usePCWindow()
  const orientation = useDeviceOrientation()
  const socket = useContext(statisticContext)
  const { webinarStatus } = useContext(WebinarSocketContext)

  const [isMuted, setIsMuted] = useState(true)
  const [isVideoStarted, setIsVideoStarted] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isVideoBug, setIsVideoBug] = useState(false)

  const playerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fakeContainerRef = useRef<HTMLDivElement>(null)

  const yaRef = useRef<IAnyObj | null>(null)

  const playVideo = () => {
    yaRef.current?.play()
    setIsVideoStarted(true)
  }
  const setVolume = (value: number) => {
    yaRef.current?.setVolume(value / 100)
  }

  const unMute = () => {
    playVideo()
    setVolume(100)
    yaRef.current?.setMuted(false)
    setIsMuted(false)
  }

  const mute = () => {
    yaRef.current?.setMuted(true)
    setIsMuted(true)
  }

  const handleClickToggle = () => (isMuted ? unMute() : mute())

  const { isFullScreen, dimension, toggleFullScreen } = useFullscreen(containerRef)
  const isHideControls = useIsHideControls(
    fakeContainerRef,
    CONTROLS_SHOW_TIME,
    CONTROLS_FIRST_SHOW_TIME,
    true
  )
  useVisibleUnmute(isVideoBug || !isVideoStarted, unMute)

  const isWebinarStarted = webinarStatus === StatusVocabulary.ACTIVE
  const isVerticalOrientation = orientation === ScreenOrientation.vertical

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://runtime.video.cloud.yandex.net/player/js/player-sdk.js'
    script.async = true

    let yaInstance: IAnyObj | null = null

    script.onload = () => {
      yaInstance = window.Ya.playerSdk.init({
        element: playerRef.current,
        source: `${videoLink}`,
        autoplay: true,
        muted: true,
        hiddenControls: '*',
      })

      if (!yaInstance) return

      yaRef.current = yaInstance
      setIsReady(true)
      yaInstance.on(PlayerEvent.STATUS_CHANGE, ({ status }: any) => {
        // if (status === YandexPlayerStatuses.INIT) setIsReady(true) по какой то причине , не прилетает (18.06.2025 - до этого ок было), поэтому setIsReady(true) пишу выше

        if (status === YandexPlayerStatuses.PLAY) {
          setIsVideoStarted(true)
          setIsVideoBug(false)
        }

        if (status === YandexPlayerStatuses.END || status === YandexPlayerStatuses.FINISHED) {
          socket?.sendEvent?.(statisticEvent[PlayerStatus.END])
        }

        if (status === YandexPlayerStatuses.FATAL || status === YandexPlayerStatuses.BROKEN) {
          setIsVideoBug(true)
        }
      })

      yaInstance.on(PlayerEvent.ERROR_CHANGE, ({ error }: any) => {
        console.log(error)
        setIsVideoBug(true)
      })
    }

    document.body.appendChild(script)

    return () => {
      if (yaInstance) yaInstance.destroy()
      document.body.removeChild(script)
    }
  }, [])

  return (
    <TranslationPlayer
      isFullScreen={isFullScreen}
      dimension={dimension}
      isPC={isPC}
      isVerticalOrientation={isVerticalOrientation}
      fakeContainerRef={fakeContainerRef}
      containerRef={containerRef}
    >
      {!isVideoBug && (
        <>
          <div
            ref={playerRef}
            className={clsx(iframeStyle.inner, iframeStyle.hideYaControls, {
              [iframeStyle.allPointerEvents]: isVideoBug,
            })}
          />
        </>
      )}
      {isReady && !isVideoBug && (
        <PlayerControls
          isHideControls={isHideControls}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          isMuted={isMuted}
          toggleMute={handleClickToggle}
          setVolume={setVolume}
        />
      )}
      {isReady && isWebinarStarted && (isMuted || !isVideoStarted || isVideoBug) && (
        <TranslationSoundButton
          onClick={handleClickToggle}
          isPaused={!isVideoStarted || isVideoBug}
        />
      )}
    </TranslationPlayer>
  )
}

export default YandexTranslation
